import React, { useState, useEffect } from "react";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import {
  addFeedback,
  getFeedbackItems,
  addFeedbackResponse,
  uploadFeedbackImage,
} from "../../firebase";

const Feedback = () => {
  const { currentUser, isAdmin } = useAuth();
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackImage, setFeedbackImage] = useState<File | null>(null);
  const [feedbackItems, setFeedbackItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [responseTexts, setResponseTexts] = useState<{ [key: string]: string }>(
    {}
  );

  // Load feedback items
  useEffect(() => {
    const loadFeedback = async () => {
      setLoading(true);
      try {
        const items = await getFeedbackItems();
        setFeedbackItems(items);
      } catch (err) {
        console.error("Error loading feedback:", err);
        setError("Failed to load feedback items");
      } finally {
        setLoading(false);
      }
    };

    loadFeedback();
  }, []);

  // Handle image selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFeedbackImage(e.target.files[0]);
    }
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setFeedbackImage(null);
  };

  // Handle feedback submission
  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackText.trim()) {
      setError("Please enter some feedback text");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      let imageUrl = "";
      if (feedbackImage) {
        imageUrl = await uploadFeedbackImage(
          currentUser?.uid || "anonymous",
          feedbackImage
        );
      }

      await addFeedback(
        feedbackText,
        imageUrl,
        currentUser?.uid || "anonymous"
      );

      // Reset form
      setFeedbackText("");
      setFeedbackImage(null);
      setSuccess("Your feedback has been submitted successfully!");

      // Reload feedback items
      const items = await getFeedbackItems();
      setFeedbackItems(items);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setError("Failed to submit feedback. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle admin response
  const handleSubmitResponse = async (feedbackId: string) => {
    const responseText = responseTexts[feedbackId];
    if (!responseText || !responseText.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await addFeedbackResponse(
        feedbackId,
        responseText,
        currentUser?.uid || ""
      );

      // Clear response text and reload feedback
      setResponseTexts((prev) => ({ ...prev, [feedbackId]: "" }));
      const items = await getFeedbackItems();
      setFeedbackItems(items);
    } catch (err) {
      console.error("Error submitting response:", err);
      setError("Failed to submit response. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  // Update response text for a specific feedback
  const handleResponseTextChange = (feedbackId: string, text: string) => {
    setResponseTexts((prev) => ({ ...prev, [feedbackId]: text }));
  };

  // Format date string
  const formatDate = (timestamp: any) => {
    if (!timestamp) return "Unknown date";

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <div className="container mx-auto px-4">
      <h1 className="text-3xl font-bold mb-4">Let's make Wattlenest better</h1>

      <p className="text-gray-600 mb-6">
        Report glitches and share your feedback here. Your posts remain
        anonymous and we will update responses on submissions as we progress.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded relative mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            className="text-red-500 hover:text-red-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded relative mb-4 flex justify-between items-center">
          <span>{success}</span>
          <button
            onClick={() => setSuccess("")}
            className="text-green-500 hover:text-green-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md mb-8">
        <div className="p-6">
          <form onSubmit={handleSubmitFeedback}>
            <div className="mb-4">
              <label
                htmlFor="feedback-text"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Your feedback
              </label>
              <textarea
                id="feedback-text"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                required
                placeholder="Share your thoughts, suggestions, or report an issue..."
              />
            </div>

            <div className="mb-4">
              <div className="flex items-center">
                <input
                  accept="image/*"
                  id="feedback-image"
                  type="file"
                  className="hidden"
                  onChange={handleImageChange}
                />
                <label htmlFor="feedback-image" className="mr-2">
                  <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
                    {feedbackImage ? "Change Image" : "Add Screenshot"}
                  </span>
                </label>
                {feedbackImage && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Remove
                  </button>
                )}
              </div>

              {feedbackImage && (
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-1">
                    Selected image: {feedbackImage.name}
                  </p>
                  <img
                    src={URL.createObjectURL(feedbackImage)}
                    alt="Selected screenshot"
                    className="max-h-48 max-w-full rounded-md"
                  />
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gold hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                submitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {submitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                "Submit Feedback"
              )}
            </button>
          </form>
        </div>
      </div>

      <h2 className="text-xl font-bold mb-4">Community Feedback</h2>

      {loading ? (
        <div className="flex justify-center my-8">
          <svg
            className="animate-spin h-8 w-8 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      ) : feedbackItems.length === 0 ? (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded">
          No feedback has been submitted yet. Be the first!
        </div>
      ) : (
        feedbackItems.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow-md mb-4">
            <div className="p-6">
              <div className="flex items-start mb-4">
                <div className="bg-gold rounded-full p-2 mr-3">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-800">{item.text}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Submitted on {formatDate(item.createdAt)}
                  </p>
                </div>
              </div>

              {item.imageUrl && (
                <div className="my-4">
                  <img
                    src={item.imageUrl}
                    alt="Feedback screenshot"
                    className="max-w-full max-h-96 rounded-md"
                  />
                </div>
              )}

              {item.response && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md">
                  <p className="text-sm font-medium text-blue-600 mb-1">
                    Admin Response:
                  </p>
                  <p className="text-gray-800">{item.response}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Responded on {formatDate(item.respondedAt)}
                  </p>
                </div>
              )}

              {isAdmin && !item.response && (
                <>
                  <div className="border-t border-gray-200 my-4 pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Add Response
                    </p>
                    <textarea
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 mb-2"
                      placeholder="Enter your response..."
                      value={responseTexts[item.id] || ""}
                      onChange={(e) =>
                        handleResponseTextChange(item.id, e.target.value)
                      }
                    />
                    <button
                      onClick={() => handleSubmitResponse(item.id)}
                      disabled={!responseTexts[item.id] || submitting}
                      className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        !responseTexts[item.id] || submitting
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      Submit Response
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default Feedback;
